/**
 * The risk register. Each risk is rated on likelihood and impact (1–5, names
 * kept in Admin) three times: inherent (before any controls), residual (now,
 * with them) and target (where it should get to, by a date). The score is
 * likelihood × impact, and its band (Low to Critical) is worked out from
 * Admin's thresholds, never typed.
 *
 * Reviews follow the risk's tier: each tier has a rhythm in months, and the
 * next review is the last one plus that. The register is issued as numbered
 * versions, frozen; movement (up, down, new) is worked out against the last
 * issue, not typed.
 */
import { addMonths, type ISODate } from "./dates";

export const RISK_LEVELS = [1, 2, 3, 4, 5] as const;

export interface RiskRating {
  /** 1–5. */
  l: number;
  /** 1–5. */
  i: number;
}

export interface RiskBand {
  name: string;
  /** The lowest score in the band. The first band starts at 1. */
  min: number;
}

/** How risks are scored and drawn, kept in Admin. */
export interface RiskScale {
  /** Names of likelihood levels 1–5. */
  likelihood: string[];
  /** Names of impact levels 1–5. */
  impact: string[];
  /** Lowest first: Low, Medium, High, Critical. */
  bands: RiskBand[];
  /** The quadrant: likelihood and impact count as high from these levels. */
  highLikelihoodFrom: number;
  highImpactFrom: number;
  /** The four zones: low likelihood and impact, likely but minor, unlikely but severe, likely and severe. */
  zones: { accept: string; manage: string; prepare: string; act: string };
}

export const DEFAULT_RISK_SCALE: RiskScale = {
  likelihood: ["Rare", "Unlikely", "Possible", "Likely", "Almost certain"],
  impact: ["Negligible", "Minor", "Moderate", "Major", "Severe"],
  bands: [
    { name: "Low", min: 1 },
    { name: "Medium", min: 5 },
    { name: "High", min: 10 },
    { name: "Critical", min: 20 },
  ],
  highLikelihoodFrom: 4,
  highImpactFrom: 4,
  zones: { accept: "Accept", manage: "Manage", prepare: "Prepare", act: "Act" },
};

export interface RiskDomain {
  id: string;
  name: string;
  /** The reference prefix: REG for REG1, REG2… */
  code: string;
  active: boolean;
  sortOrder: number;
}

export interface RiskTier {
  id: string;
  /** "Tier 1": the first is the board's. */
  name: string;
  /** How often its risks are reviewed. */
  reviewMonths: number;
  active: boolean;
  sortOrder: number;
}

export type ControlEffectiveness = "working" | "partly" | "not_working" | "untested";
export const CONTROL_LABEL: Record<ControlEffectiveness, string> = { working: "Working", partly: "Partly working", not_working: "Not working", untested: "Not tested" };

export interface RiskControl {
  id: string;
  what: string;
  ownerId: string | null;
  effectiveness: ControlEffectiveness;
}

export interface Risk {
  id: string;
  domainId: string;
  /** Numbered within its area: REG1, REG2. */
  number: number;
  /** The risk itself, in a sentence. */
  statement: string;
  notes: string;
  ownerId: string | null;
  tierId: string;
  inherent: RiskRating | null;
  residual: RiskRating;
  target: RiskRating | null;
  targetBy: ISODate | null;
  controls: RiskControl[];
  status: "open" | "closed";
  closedAt: string | null;
  closedNote: string | null;
  /** The last review; created counts as reviewed. */
  lastReviewedOn: ISODate;
  /** When its residual rating last rose into the top two bands, and who rated it: the other directors are told. */
  escalatedAt: string | null;
  escalatedBy: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A risk as an issue froze it. */
export interface IssuedRisk {
  id: string;
  ref: string;
  statement: string;
  domain: string;
  tier: string;
  owner: string | null;
  inherent: RiskRating | null;
  residual: RiskRating;
  target: RiskRating | null;
  targetBy: ISODate | null;
  controls: { what: string; owner: string | null; effectiveness: ControlEffectiveness }[];
  lastReviewedOn: ISODate;
  /** Since the issue before it. */
  movement: Movement;
}

export interface RiskIssueSummary {
  id: string;
  version: string;
  note: string;
  issuedBy: string | null;
  issuedByName: string | null;
  issuedAt: string;
  count: number;
  /** Open risks by band name. */
  byBand: Record<string, number>;
}

export interface RiskIssue extends RiskIssueSummary {
  scale: RiskScale;
  risks: IssuedRisk[];
  /** Risks closed since the issue before. */
  closed: { id: string; ref: string; statement: string }[];
}

/** The last issue's residual rating for each risk, to work out movement. */
export interface LastIssue {
  id: string;
  version: string;
  issuedAt: string;
  residual: Record<string, RiskRating>;
}

export const riskRef = (r: Pick<Risk, "number">, domain: Pick<RiskDomain, "code"> | undefined) => `${domain?.code ?? "R"}${r.number}`;
export const score = (r: RiskRating) => r.l * r.i;

/** The band a score falls in, and its position (0 = lowest). */
export function bandOf(s: number, scale: Pick<RiskScale, "bands">): { band: RiskBand; index: number } {
  let index = 0;
  scale.bands.forEach((b, k) => {
    if (s >= b.min) index = k;
  });
  return { band: scale.bands[index]!, index };
}

export type RiskZone = keyof RiskScale["zones"];

export function zoneOf(r: RiskRating, scale: Pick<RiskScale, "highLikelihoodFrom" | "highImpactFrom">): RiskZone {
  const likely = r.l >= scale.highLikelihoodFrom;
  const severe = r.i >= scale.highImpactFrom;
  return likely && severe ? "act" : severe ? "prepare" : likely ? "manage" : "accept";
}

/** The next review: the last one plus its tier's rhythm. */
export function nextReview(r: Pick<Risk, "lastReviewedOn" | "tierId">, tiers: Pick<RiskTier, "id" | "reviewMonths">[]): ISODate {
  const months = tiers.find((t) => t.id === r.tierId)?.reviewMonths ?? 12;
  return addMonths(r.lastReviewedOn, months);
}

export type ReviewState = "overdue" | "due_soon" | "ok";
export function reviewState(next: ISODate, today: ISODate, soon: ISODate): ReviewState {
  return next < today ? "overdue" : next <= soon ? "due_soon" : "ok";
}

export type Movement = "new" | "up" | "down" | "same";
export const MOVEMENT_LABEL: Record<Movement, string> = { new: "New", up: "Up", down: "Down", same: "No change" };

/** Against the last issue's residual score: new if it wasn't in it. */
export function movementOf(r: Pick<Risk, "id" | "residual">, last: Pick<LastIssue, "residual"> | null): Movement {
  const was = last?.residual[r.id];
  if (!last || !was) return last ? "new" : "same";
  const d = score(r.residual) - score(was);
  return d > 0 ? "up" : d < 0 ? "down" : "same";
}

/** Above its target: residual scores more than the target. */
export const aboveTarget = (r: Pick<Risk, "residual" | "target">) => !!r.target && score(r.residual) > score(r.target);
